export const CHUNK_ERROR_RELOAD_KEY = '__chunkErrorReload';
export const CHUNK_ERROR_PATH_KEY = '__chunkErrorPath';
export const CHUNK_ERROR_LAST_RELOAD_KEY = '__chunkErrorLastReload';

const RELOAD_COOLDOWN_MS = 10000;

/**
 * Detect chunk load failures from webpack lazy imports, including when a missing
 * chunk returns HTML (SPA fallback) and the browser throws SyntaxError.
 */
export const isChunkLoadError = (error) => {
  if (!error) {
    return false;
  }

  if (error.name === 'ChunkLoadError') {
    return true;
  }

  if (error instanceof SyntaxError && error.message?.includes("Unexpected token '<'")) {
    return true;
  }

  const message = error.message;
  if (!message) {
    return false;
  }

  return (
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    (message.includes('chunk') && message.includes('failed'))
  );
};

/**
 * Detect chunk SyntaxError from window "error" events when event.error is absent.
 */
export const isChunkScriptErrorEvent = (event) => {
  if (!event?.message?.includes("Unexpected token '<'")) {
    return false;
  }

  return Boolean(event.filename?.includes('.chunk.js'));
};

export const hasRecentChunkReload = () => {
  const lastReload = sessionStorage.getItem(CHUNK_ERROR_LAST_RELOAD_KEY);
  if (!lastReload) {
    return false;
  }

  return Date.now() - Number(lastReload) < RELOAD_COOLDOWN_MS;
};

/**
 * Reload the page to fetch fresh main.js and chunk hashes after a deploy.
 * Returns true if reload was initiated, false if loop guard blocked it.
 */
export const reloadPageForStaleChunks = () => {
  if (hasRecentChunkReload()) {
    return false;
  }

  const currentPath = window.location.pathname + window.location.search + window.location.hash;
  sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, 'true');
  sessionStorage.setItem(CHUNK_ERROR_PATH_KEY, currentPath);
  sessionStorage.setItem(CHUNK_ERROR_LAST_RELOAD_KEY, String(Date.now()));
  window.location.reload();
  return true;
};

/**
 * Restore saved route path after a chunk-error reload (call once on app boot).
 */
export const restorePathAfterChunkReload = () => {
  if (sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY) !== 'true') {
    return;
  }

  const savedPath = sessionStorage.getItem(CHUNK_ERROR_PATH_KEY);
  const currentPath = window.location.pathname + window.location.search + window.location.hash;

  sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
  sessionStorage.removeItem(CHUNK_ERROR_PATH_KEY);

  if (savedPath && savedPath !== currentPath) {
    window.history.replaceState(null, '', savedPath);
  }
};
